import { Palette, Type, Sparkles, Image as ImageIcon, Layers, Wand2, Mic } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";

const PALETTES = [
  { name: "Premier Aqua", hex: ["#061414", "#0B2F2C", "#27E6D1", "#B9FF2F"] },
  { name: "Founder Black", hex: ["#080808", "#1A1A1A", "#FFFFFF", "#FFC400"] },
  { name: "Coach Mint", hex: ["#063D38", "#0BAF83", "#C7FFF3", "#F4FFFC"] },
  { name: "Direct Response", hex: ["#0F0F10", "#FF3D2E", "#FFE0B2", "#FFFFFF"] },
];

const FONTS = [
  { primary: "Outfit", secondary: "Manrope", tag: "SaaS Premium" },
  { primary: "Playfair Display", secondary: "Inter", tag: "Authority Author" },
  { primary: "Space Grotesk", secondary: "IBM Plex Sans", tag: "Tech Forward" },
];

export default function BrandLab() {
  return (
    <div data-testid="brand-lab-page" className="space-y-10">
      <PageHeader
        kicker="BrandLab"
        title="Your brand intelligence hub"
        description="Logos, palettes, fonts, voice, templates, and AI-generated background systems — one source of truth for every Lab."
        actions={
          <button className="inline-flex items-center gap-2 px-4 h-11 rounded-xl pl-btn-aqua text-sm">
            <Wand2 className="h-4 w-4" /> Auto-fill Brand Kit
          </button>
        }
      />

      <section className="space-y-3">
        <Eyebrow>Color Palettes</Eyebrow>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PALETTES.map((p, i) => (
            <div
              key={i}
              data-testid={`palette-${i}`}
              className="rounded-2xl overflow-hidden border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 pl-lift"
            >
              <div className="grid grid-cols-4 h-24">
                {p.hex.map((c) => (
                  <div key={c} style={{ background: c }} />
                ))}
              </div>
              <div className="p-4">
                <div className="text-sm font-medium text-[var(--pl-text)]">{p.name}</div>
                <div className="text-[11px] text-[var(--pl-text-muted)] font-mono">{p.hex.join("  ·  ")}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <Eyebrow>Typography Pairings</Eyebrow>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {FONTS.map((f, i) => (
            <div
              key={i}
              data-testid={`font-${i}`}
              className="rounded-2xl p-6 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 pl-lift"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-lime)]">{f.tag}</div>
              <div className="font-display text-3xl font-semibold text-[var(--pl-text)] mt-2" style={{ fontFamily: f.primary }}>
                {f.primary}
              </div>
              <div className="text-sm text-[var(--pl-text-muted)] mt-2" style={{ fontFamily: f.secondary }}>
                {f.secondary} · The body pairing for premium long-form, slides, flyers.
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card icon={Sparkles} title="Brand Voice" desc="Premium, direct, business-led. No hype, no fluff." />
        <Card icon={ImageIcon} title="Logo System" desc="Light / dark / monochrome variants, faded watermark, diagonal pattern." />
        <Card icon={Layers} title="Background Generator" desc="Glassmorphism, gradients, faded logo backgrounds for slides & covers." />
        <Card icon={Type} title="CTA Style" desc="Aqua and lime CTAs only, glow on hover, 8px radius." />
        <Card icon={Palette} title="Approved Phrases" desc="Operator's Playbook · Direct response · Authority · Build & launch." />
        <Card icon={Mic} title="Tone Examples" desc="Audio voice samples for narration, intros, and webinar voiceover." />
      </section>
    </div>
  );
}

function Eyebrow({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">{children}</div>;
}

function Card({ icon: I, title, desc }) {
  return (
    <div
      data-testid={`brand-card-${title.replace(/\s+/g, "-").toLowerCase()}`}
      className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 backdrop-blur-md pl-lift pl-contour-card"
    >
      <div className="h-10 w-10 rounded-xl bg-[var(--pl-aqua)]/10 border border-[var(--pl-aqua)]/30 grid place-items-center mb-3">
        <I className="h-5 w-5 text-[var(--pl-aqua)]" />
      </div>
      <div className="font-medium text-[var(--pl-text)]">{title}</div>
      <div className="text-xs text-[var(--pl-text-muted)] mt-1.5">{desc}</div>
    </div>
  );
}
